/**
 * 69.3b, the dropship's look (DECISIONS 167, 196: a spendable reinforcement that drops a squad at a called place). The
 * owner's brief (Oct 18): *"I like the modular payload, vehicle or troop container.  But the wings are probably too far out
 * and it's a little too direct world war 2 ish … sort of like halo dropships but they're slightly too halo-ey but good boxy
 * with thrusters design … Initial shouldn't be that big, just big enough to carry an apc or maybe 6-12 troops in a
 * container, but not both"*; and his pick from the first sheet's three drives (Oct 18): *"Ok for dropship, sort of
 * thinking B base design, with quad C nacelles as well, but probably wider and shorter"*. One compact, boxy lifter in the
 * D style (65.36 / 67.60, rendering/vehicleLooks.ts: side profiles extruded across the body and chamfered, bare, separate
 * moving parts):
 *   - **the airframe** (B's): a wide flat body over the payload slot (its underside the strongback the clamps hang from),
 *     the cab sitting on its nose over the payload's front (two pilots, glass ahead and down past the chin) rather than
 *     hanging ahead of it — so it is short nose to tail; a short tail with two small canted fins; no wings;
 *   - **the lift** (B's and C's): lift jets in the side sponsons under louvres (shut flat in cruise), two tail nozzles that
 *     swivel down, and **four swivelling nacelles** on short stubs at the sponsons' corners, two each side (C's, as a quad);
 *   - **one payload slot** under the body, sized by the 6×6 it must carry: a **troop container** (`buildTroopContainer`:
 *     12 seats facing in, a rear ramp) or **one vehicle** (`mountVehicle`: measured, refused if it doesn't fit), never both;
 *     held by four clamp arms that reach down to the payload's grip (telescoping to its height), lowered on four cables;
 *   - **four landing legs** that fold forward under the sponsons and stand the ship over its payload (the payload's floor
 *     on the ground when it lands, so the squad walks straight out of the container).
 * Moving parts (`updateDropship`): the nacelles and tail nozzles tilting from cruise to hover, the louvres, the nacelles'
 * fans turning, the legs, the clamps opening, the payload lowered on its cables, the container's ramp, the glows by thrust.
 *
 * Person units (a person is 1.8), +z forward; the origin on the ground under the payload slot when it stands on its legs
 * (the payload's floor). Presentation only — the sheet is design_sheets/dropship.html; the sim (its flight, seats, hit
 * shape, payloads) is lane C's (BUILDLIST 69.3b).
 */
import * as THREE from 'three';
import { bakeStaticParts } from './meshBake';
import type { UnitPalette } from './unitPalette';
import { box, group, anchor, type VehicleModel } from './vehicleModels';
import { slab, front, top, ring, rect, rev, cyl, type Pts } from './vehicleLooks';
import { DROPSHIP_ANCHORS } from './dropshipAnchors';

/** The measures (person units, the ship's frame). */
export const DROPSHIP = {
  /**
   * the body's underside (the strongback) above the payload's floor: the payload stands under it. 69.3b's frame fit (the
   * owner: *"For m113 I wouldn't have a problem expanding dropship to allow for it to fit"*): raised from 3.5 so a vehicle in
   * its shipping frame (rendering/vehicleFrame.ts, 7.6 × 3.5 × 4.0 outside) hangs under it, the legs grown with it
   */
  belly: 4.25,
  /**
   * the airframe's rise over its drawn line (its profiles are drawn with the strongback at 3.35; the hull rides `lift`
   * higher — 0.15 took an M113 with its .50 on the cupola; 0.9, the M113 in its frame) — the payload, legs, clamps and
   * cables are measured from the ground, the airframe's parts in the hull's frame
   */
  lift: 0.9,
  /** the body's half-width; the sponsons' centre line (x) and half-width */
  half: 1.8, sponsonX: 2.15, sponsonHalf: 0.35,
  /** the payload slot's centre along the body (z) — the lift's centre */
  slotZ: -0.2,
  /** the most a payload may measure: along, across (inside the legs), up (under the strongback) — a framed vehicle's 7.6 × 3.5 × 4.0 and a hand */
  slot: { length: 7.8, width: 3.7, height: 4.1 },
  /** the clamp arms' hinge line (x; outside the frame's corner posts, under the sponsons' inner edge) and their z either side of the slot's centre, at most */
  clampX: 1.95, clampZ: 2.4,
  /** the legs' hinges (x, z) under the sponsons; they fold forward */
  legX: 2.15, legZ: [0.75, -3.2] as const,
  /** the four nacelles: their shafts' line (x), height and z (front pair, rear pair) */
  nacelle: { x: 3.05, y: 4.05, z: [1.75, -2.55] as const },
  /** the troop container: length, width, height, its floor's top, its seats' z along each side */
  box: { length: 6.2, width: 2.7, height: 2.8, floor: 0.25, seatZ: [-2.15, -1.35, -0.55, 0.25, 1.05, 1.85] },
};
const STEEL = '#5e615e', RUBBER = '#1d1e1f', GLASS = '#1c2328', GLOW = '#ffd49a', INSIDE = '#2a2d30', LAMP = '#d8463a';
/** The airframe's own colours (69.3b, the ship's crash: the wreck is built from the same parts). */
export const DROPSHIP_COLOURS = { STEEL, RUBBER, GLASS, INSIDE } as const;

export type DropshipPayload = 'troops' | 'vehicle' | 'empty';

/** A thruster part that turns from cruise (0) to hover (1): `hover` its angle about x at full hover (−π/2: aft turned down). */
interface Tilter { group: THREE.Group; hover: number; cruise?: number }
/** A glow by phase: `lift` shows in the hover, `cruise` in cruise, `both` always with the thrust. */
interface Glow { mesh: THREE.Mesh; phase: 'lift' | 'cruise' | 'both'; r: number }

export interface DropshipModel {
  root: THREE.Group;
  full: THREE.Group;
  silhouette: THREE.Group;
  /** rides the flight's bank and pitch; everything hangs off it */
  hull: THREE.Group;
  tilters: Tilter[];
  /** the nacelles' fans, turned about their own z by the thrust */
  fans: THREE.Group[];
  /** the four nacelles in the sim's order (core/shipBody.ts SHIP_PARTS: front left, front right, rear left, rear right; +x is the ship's left) — the crash hides those torn off */
  nacelles: THREE.Group[];
  glows: Glow[];
  /** the four legs, each turned about x (0 down, −π/2 folded forward) */
  legs: THREE.Group[];
  /** each leg's oleo and pad, slid up into its sleeve by `retract` as it folds (the frame fit's taller legs fold as short as before) */
  slides: Array<{ group: THREE.Group; retract: number }>;
  /** the four clamp arms, each turned about z by `side` × 0.9 × the open share; `bar` its telescoping inner bar, `hook` its jaw */
  clamps: Array<{ group: THREE.Group; side: 1 | -1; end: 1 | -1; bar: THREE.Mesh; hook: THREE.Mesh }>;
  /** the four cables (one unit-tall box each, scaled to the payout) */
  cables: Array<{ mesh: THREE.Mesh; side: 1 | -1; end: 1 | -1 }>;
  /** where the payload hangs (the payload's floor at its origin); `lower` moves it down */
  mount: THREE.Group;
  /** the pilots' seats (hidden in the cab) */
  pilots: THREE.Object3D[];
  /** what it carries: its root and that root's place in the slot, the clamps' grip (height, half-width) and z, its size */
  payload: { kind: DropshipPayload; root: THREE.Object3D | null; at: THREE.Vector3; container?: TroopContainer; vehicle?: VehicleModel; grip: { y: number; x: number }; clampZ: number; size: THREE.Vector3 } | null;
  /** the fans' turn so far (radians) */
  spin: number;
}

/**
 * The flight's pose: shares 0 … 1 (`tilt` 0 cruise … 1 hover, on to `NACELLE_TILT.forward` tipped ahead to brake), `lower`
 * person units paid out, `bank` / `pitch` radians.
 */
export interface DropshipMotion { tilt?: number; thrust?: number; gear?: number; clamps?: number; lower?: number; ramp?: number; bank?: number; pitch?: number; cables?: boolean }

/** A pane of glass along a profile's face from `a` to `b` (z, y), proud of it, inset from its ends. */
function pane(parent: THREE.Object3D, a: [number, number], b: [number, number], w: number, x = 0, inset = 0.1, t = 0.025): THREE.Mesh {
  const dz = b[0] - a[0], dy = b[1] - a[1], len = Math.hypot(dz, dy), uz = dz / len, uy = dy / len, nz = uy, ny = -uz;
  const p0: [number, number] = [a[0] + uz * inset, a[1] + uy * inset], p1: [number, number] = [b[0] - uz * inset, b[1] - uy * inset];
  return slab(parent, [p0[0], p0[1], p1[0], p1[1], p1[0] + nz * t, p1[1] + ny * t, p0[0] + nz * t, p0[1] + ny * t], w, GLASS, x);
}
function glow(m: DropshipModel, parent: THREE.Object3D, r: number, x: number, y: number, z: number, phase: Glow['phase']): THREE.Mesh {
  const g = new THREE.Mesh(new THREE.CircleGeometry(r, 14), new THREE.MeshBasicMaterial({ color: GLOW, transparent: true, opacity: 0.8, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide }));
  g.rotation.y = Math.PI;   // facing aft (−z) in its part's frame
  g.position.set(x, y, z);
  g.userData.noBake = true;
  parent.add(g);
  m.glows.push({ mesh: g, phase, r });
  return g;
}

// ---------------------------------------------------------------------------
// The airframe
// ---------------------------------------------------------------------------

/** The body: wide and flat over the slot, its nose chamfered under the cab. */
export const BODY: Pts = [-3.9, 3.35, 3.5, 3.35, 4.05, 3.75, 4.1, 4.3, 3.4, 4.62, -3.3, 4.62, -3.9, 4.3];
/** The cab, on the body's nose: its chin glass down to the front, the windscreen raked back. */
export const CAB: Pts = [1.7, 4.4, 4.0, 3.55, 4.7, 3.95, 4.8, 4.35, 4.1, 5.15, 1.7, 5.25];
export const TAIL: Pts = [-4.05, 3.6, -3.3, 3.6, -3.3, 4.55, -4.75, 4.55, -4.95, 4.2, -4.65, 3.85];
/** A sponson along each side of the body, the lift jets in it. */
export const SPONSON: Pts = [-3.45, 3.42, 2.45, 3.42, 2.85, 3.85, 2.7, 4.5, -3.25, 4.5, -3.65, 3.95];

function airframe(m: DropshipModel, p: UnitPalette): void {
  const { hull } = m, D = DROPSHIP;
  slab(hull, BODY, 2 * D.half, p.body, 0, 0.12).name = 'body';
  slab(hull, CAB, 2.6, p.body, 0, 0.1).name = 'cab';
  slab(hull, TAIL, 2.2, p.body, 0, 0.1).name = 'tail';
  // the strongback: a dark plate under the body, its two rails
  slab(hull, rect(-3.8, 3.27, 3.4, 3.37), 2 * D.half - 0.2, p.dark, 0, 0.03);
  for (const side of [-1, 1]) box(hull, 0.18, 0.14, 6.6, STEEL, side * (D.clampX - 0.12), 3.24, D.slotZ);
  // the cab's glass: the windscreen, the chin windows, a side window and the crew door each side
  pane(hull, [4.8, 4.35], [4.1, 5.15], 2.3);
  pane(hull, [4.0, 3.55], [4.7, 3.95], 1.8, 0, 0.12);
  for (const side of [-1, 1]) {
    slab(hull, [3.3, 4.68, 4.45, 4.68, 4.05, 5.08, 3.3, 5.1], 0.06, GLASS, side * 1.3);   // (half sunk: its face 3 cm off the cab's, as every pair of faces here)
    slab(hull, rect(2.0, 4.66, 2.9, 5.15), 0.03, p.body, side * 1.315, 0.012, [rev(rect(2.2, 4.8, 2.7, 5.05))]);   // the crew door, its window
    slab(hull, rect(2.2, 4.8, 2.7, 5.05), 0.03, GLASS, side * 1.315);
    slab(hull, rect(-3.3, 4.12, 2.6, 4.3), 0.02, p.flash, side * (D.half + 0.01));                                 // the team's band
    box(hull, 0.06, 0.1, 0.12, LAMP, side * 1.11, 4.25, -4.85);                                                     // the tail light
  }
  // the chin's sensor ball, the cab's front lamp
  top(hull, ring(0, 0, 0.2, 8, Math.PI / 8), 0.22, p.dark, 3.15, 0.05).position.set(0, 3.15, 3.7);
  box(hull, 0.5, 0.08, 0.04, '#e8e2c8', 0, 3.98, 4.71).rotation.x = 0.5;
  // the fins: two small ones on the tail, canted out
  for (const side of [-1, 1]) {
    const f = group(hull, side * 0.8, 4.5, 0); f.rotation.z = -side * 0.42;
    slab(f, [-3.6, 0, -4.6, 0, -4.8713, 0.62, -4.2588, 0.62], 0.12, p.body, 0, 0.03);
    slab(f, [-4.2588, 0.62, -4.8713, 0.62, -4.95, 0.8, -4.45, 0.8], 0.18, p.flash);   // the tip, capping the fin
  }
  for (const side of [-1, 1]) m.pilots.push(anchor(hull, side * 0.6, 3.95, 3.5));
}

/** A landing leg at (x, z) under a sponson: a sleeve, the oleo, the pad; folds forward about x, the oleo telescoping in. */
function leg(m: DropshipModel, p: UnitPalette, side: number, z: number): void {
  const D = DROPSHIP, fold = group(m.hull, side * D.legX, D.belly - D.lift - 0.02, z);
  const splay = group(fold); splay.rotation.z = side * 0.09;
  const L = (D.belly - 0.02) / Math.cos(0.09);
  slab(splay, [-0.16, 0, 0.16, 0, 0.1, -1.9, -0.1, -1.9], 0.2, p.dark, 0, 0.03);       // the sleeve
  box(fold, 0.24, 0.16, 0.2, p.dark, 0, 0, 0);                                         // the hinge's knuckle (with the sleeve: one draw)
  // the oleo and the pad (one draw): folded, they slide up into the sleeve to the length the legs had under a 3.5 belly
  const slide = group(splay);
  cyl(slide, 0.07, L - 1.85, STEEL, 0, -1.85 - (L - 1.85) / 2 + 0.05, 0, 'y', 8);      // the oleo
  slab(slide, [-0.4, -L, 0.4, -L, 0.3, -L + 0.12, -0.3, -L + 0.12], 0.5, STEEL, 0, 0.03);   // the pad
  m.legs.push(fold);
  m.slides.push({ group: slide, retract: Math.max(0, L - LEG_FOLDED) });
}
/** A folded leg's reach (person units): the sleeve and the oleo drawn in — the legs' length before the frame fit. */
const LEG_FOLDED = (3.5 - 0.02) / Math.cos(0.09);

/** A clamp arm on the strongback's edge, hanging to the payload's grip: the sleeve, its telescoping bar and the jaw. */
function clamp(m: DropshipModel, p: UnitPalette, side: 1 | -1, end: 1 | -1): void {
  const D = DROPSHIP, g = group(m.hull, side * D.clampX, D.belly - D.lift - 0.08, D.slotZ + end * D.clampZ);
  slab(g, rect(-0.15, -0.55, 0.15, 0.05), 0.18, STEEL, 0, 0.03);
  const bar = box(g, 0.1, 1, 0.12, p.dark, 0, -0.5, 0);
  const hook = box(g, 0.3, 0.1, 0.24, p.dark, -side * 0.12, -0.6, 0);
  m.clamps.push({ group: g, side, end, bar, hook });
  const cable = box(m.hull, 0.035, 1, 0.035, RUBBER, side * (D.clampX - 0.12), D.belly - D.lift - 0.1, D.slotZ + end * D.clampZ);
  cable.userData.noBake = true; cable.visible = false;
  m.cables.push({ mesh: cable, side, end });
}

// ---------------------------------------------------------------------------
// The lift
// ---------------------------------------------------------------------------

/** The sponsons and their lift jets (intake grilles on top, louvres under, vectoring fore and aft, shut flat in cruise). */
function liftJets(m: DropshipModel, p: UnitPalette): void {
  const D = DROPSHIP;
  for (const side of [-1, 1]) {
    slab(m.hull, SPONSON, 2 * D.sponsonHalf, p.body, side * D.sponsonX, 0.08);
    for (const z of [0.3, -1.3]) {
      top(m.hull, rect(side * (D.sponsonX - 0.24), z - 0.45, side * (D.sponsonX + 0.24), z + 0.45), 0.03, INSIDE, 4.5);
      for (const k of [-0.28, 0, 0.28]) box(m.hull, 0.56, 0.04, 0.05, STEEL, side * D.sponsonX, 4.55, z + k);
      const lv = group(m.hull, side * D.sponsonX, 3.4, z);
      m.tilters.push({ group: lv, hover: 0, cruise: Math.PI / 2 - 0.08 });
      for (const k of [-0.33, -0.11, 0.11, 0.33]) box(lv, 0.5, 0.2, 0.03, p.dark, 0, -0.06, k);
      const under = group(m.hull, side * D.sponsonX, 3.42, z); under.rotation.x = Math.PI / 2;   // (the lift glow faces down)
      glow(m, under, 0.3, 0, 0, 0.03, 'lift');   // (3 cm under the sponson: 2 cm sat on the detector's line)
    }
  }
}

/** The tail's two nozzles: short swivelling ducts, thrust aft in cruise and down in the hover. */
function tailNozzles(m: DropshipModel, p: UnitPalette): void {
  for (const side of [-1, 1]) {
    const n = group(m.hull, side * 0.62, 4.02, -4.65);
    m.tilters.push({ group: n, hover: -Math.PI / 2 });
    cyl(n, 0.32, 0.6, STEEL, 0, 0, -0.26, 'z', 10);
    cyl(n, 0.25, 0.6, RUBBER, 0, 0, -0.3, 'z', 10);                                         // (its liner 4 cm past the duct at the mouth, short of it inside)
    for (const k of [0, 1]) cyl(n, 0.36, 0.04, p.dark, 0, 0, -0.06 - k * 0.22, 'z', 10);   // its swivel rings
    glow(m, n, 0.25, 0, 0, -0.63, 'both');   // (3 cm past the liner: on it, the depth buffer fights)
  }
}

/** The nacelle's side profile (z, y about its shaft): boxy, chamfered, its intake ahead and nozzle aft. */
export const NACELLE: Pts = [1.3, -0.33, 1.15, 0.42, 0.45, 0.5, -1.0, 0.45, -1.35, 0.26, -1.35, -0.26, -1.0, -0.42, 0.45, -0.46, 1.15, -0.42];
/** The four swivelling nacelles, two each side on stubs off the sponsons (thrust aft in cruise, down in the hover). */
function nacelles(m: DropshipModel, p: UnitPalette): void {
  const N = DROPSHIP.nacelle, D = DROPSHIP;
  for (const side of [-1, 1]) for (const z of N.z) {
    slab(m.hull, [z - 0.35, 3.82, z + 0.35, 3.82, z + 0.25, 4.3, z - 0.25, 4.3], N.x - 0.4 - (D.sponsonX + D.sponsonHalf) + 0.1, p.body, side * ((N.x - 0.4 + D.sponsonX + D.sponsonHalf) / 2), 0.04);   // the stub
    cyl(m.hull, 0.22, 0.24, STEEL, side * (N.x - 0.5), N.y, z, 'x', 10);                                       // the shaft's collar
    const g = group(m.hull, side * N.x, N.y, z);
    m.tilters.push({ group: g, hover: -Math.PI / 2 });
    m.nacelles[(side > 0 ? 0 : 1) + (z === N.z[0] ? 0 : 2)] = g;
    m.fans.push(nacelleBody(g, p));
    glow(m, g, 0.25, 0, 0, -1.72, 'both');
  }
}
/** A nacelle's housing, band, intake, fan and nozzle in `g` (its shaft at the origin, +z its intake); returns the fan. (69.3b, the ship's crash: a torn-off one lies by the wreck.) */
export function nacelleBody(g: THREE.Object3D, p: UnitPalette): THREE.Group {
  slab(g, NACELLE, 0.8, p.body, 0, 0.08);
  slab(g, rect(-0.9, 0.42, 0.3, 0.47), 0.82, p.flash);                                                        // the team's band
  front(g, ring(0, 0, 0.35, 12), 0.08, RUBBER, 1.27);                                                         // the intake's mouth
  const fan = group(g, 0, 0, 1.3);
  cyl(fan, 0.11, 0.08, STEEL, 0, 0, 0, 'z', 8);
  for (let i = 0; i < 5; i++) { const b = group(fan); b.rotation.z = (i * 2 * Math.PI) / 5; box(b, 0.12, 0.26, 0.02, '#3a3d40', 0, 0.18, 0).rotation.y = 0.5; }
  cyl(g, 0.3, 0.34, STEEL, 0, 0, -1.48, 'z', 10);                                                            // the nozzle
  cyl(g, 0.24, 0.36, RUBBER, 0, 0, -1.51, 'z', 10);
  return fan;
}

// ---------------------------------------------------------------------------
// The payloads
// ---------------------------------------------------------------------------

export interface TroopContainer {
  root: THREE.Group;
  /** the rear ramp, hinged at the floor's back edge: 0 shut … 1 down on the ground */
  ramp: THREE.Group;
  /** the twelve seats: a seated passenger's feet on the floor (the container's frame), `side` the wall they're on (they face in) */
  seats: Array<{ at: THREE.Object3D; side: 1 | -1 }>;
  /** where each passenger stands once out, on the ground behind the ramp (the container's frame) */
  exits: THREE.Object3D[];
}
/** The ramp's open angle: from shut (up) to its lip on the ground. */
const rampDown = (len: number) => -(Math.PI / 2 + Math.asin(DROPSHIP.box.floor / len));

/** The troop container: a ribbed box on two skids, twelve seats facing in along its walls, a rear ramp, the grip rails on top. */
export function buildTroopContainer(p: UnitPalette): TroopContainer {
  const B = DROPSHIP.box, hl = B.length / 2, hw = B.width / 2;
  const root = new THREE.Group();
  const c: TroopContainer = { root, ramp: group(root, 0, B.floor, -hl + 0.05), seats: [], exits: [] };
  slab(root, rect(-hl, B.floor - 0.1, hl, B.floor), B.width, p.dark, 0, 0.02);                            // the floor
  for (const side of [-1, 1]) slab(root, [-hl + 0.3, 0, hl - 0.3, 0, hl - 0.05, B.floor - 0.1, -hl + 0.05, B.floor - 0.1], 0.22, STEEL, side * (hw - 0.3), 0.03);   // the skids
  slab(root, rect(-hl, B.height - 0.12, hl, B.height), B.width, p.body, 0, 0.04);                          // the roof
  front(root, rect(-hw, B.floor, hw, B.height - 0.1), 0.1, p.body, hl - 0.05, 0.03);                        // the front wall
  for (const side of [-1, 1]) {
    slab(root, rect(-hl + 0.08, B.floor, hl - 0.08, B.height - 0.08), 0.08, p.body, side * (hw - 0.04), 0.02, [rev(rect(-1.2, 1.75, -0.6, 1.95)), rev(rect(0.6, 1.75, 1.2, 1.95))]);   // the wall, two vision slits
    for (const z of [-1.2, 0.6]) slab(root, rect(z, 1.75, z + 0.6, 1.95), 0.02, GLASS, side * (hw - 0.04));
    for (let k = 0; k <= 7; k++) box(root, 0.06, B.height - B.floor - 0.25, 0.1, p.body, side * (hw + 0.03), (B.height + B.floor) / 2 - 0.05, -hl + 0.35 + k * ((B.length - 0.7) / 7));   // the ribs
    slab(root, rect(-hl + 0.2, 1.05, hl - 0.2, 1.3), 0.03, p.flash, side * (hw + 0.015));                     // the band, on the wall under the ribs (3 cm off each)
    box(root, 0.12, 0.12, B.length - 0.3, STEEL, side * (hw + 0.02), B.height - 0.06, 0).name = 'grip';    // the grip rail the clamps take
    for (const e of [-1, 1]) box(root, 0.08, B.height - 0.05, 0.14, p.dark, side * hw, B.height / 2, e * (hl - 0.1));   // the corner posts (sunk in the wall inside, 5 cm past its ends)
    // inside: the bench along the wall, its back, the seats on it, a grab rail overhead
    slab(root, rect(-2.55, B.floor + 0.38, 2.25, B.floor + 0.46), 0.42, INSIDE, side * (hw - 0.3));
    slab(root, rect(-2.55, B.floor + 0.6, 2.25, B.floor + 1.35), 0.06, INSIDE, side * (hw - 0.12));
    box(root, 0.04, 0.04, 4.8, STEEL, side * 0.55, B.height - 0.35, -0.15);
    for (const z of B.seatZ) {
      const at = anchor(root, side * (hw - 0.42), B.floor, z);   // (the seated pose's hips sit 0.55 over its feet: on the bench)
      at.rotation.y = side > 0 ? -Math.PI / 2 : Math.PI / 2;
      c.seats.push({ at, side: side as 1 | -1 });
    }
  }
  box(root, 0.6, 0.04, 0.08, LAMP, 0, B.height - 0.14, 0.5).name = 'jumpLight';                             // the jump light (207 (7): the ride-in cabin lights it)
  // the ramp: a chamfered plate, its ribs inside (the tread, when down), the band across its top
  const rampLen = B.height - B.floor - 0.12;
  front(c.ramp, rect(-hw + 0.08, 0, hw - 0.08, rampLen), 0.1, p.body, 0, 0.025);
  for (let k = 1; k <= 5; k++) box(c.ramp, B.width - 0.4, 0.04, 0.04, STEEL, 0, (k * rampLen) / 6, 0.07);
  box(c.ramp, B.width - 0.08, 0.12, 0.12, p.flash, 0, rampLen - 0.2, -0.04);
  c.ramp.userData.len = rampLen;
  // out behind the ramp: two files fanning out
  for (let i = 0; i < 12; i++) c.exits.push(anchor(root, (i % 2 ? 1 : -1) * (0.9 + 0.9 * Math.floor(i / 4)), 0, -hl - 3.4 - 1.0 * (Math.floor(i / 2) % 2) - 0.6 * Math.floor(i / 4)));
  return c;
}

/** Hang a payload in the slot: its grip under the clamps, the clamps reaching to it; the cables to the same points. */
function setPayload(m: DropshipModel, kind: DropshipPayload, root: THREE.Object3D | null, grip: { y: number; x: number }, clampZ: number, size: THREE.Vector3): void {
  const D = DROPSHIP;
  for (const c of m.mount.children.slice()) m.mount.remove(c);
  if (root) m.mount.add(root);
  const reach = D.belly - 0.08 - grip.y;
  for (const c of m.clamps) {
    c.group.position.z = D.slotZ + c.end * clampZ;
    c.bar.scale.y = Math.max(0.2, reach - 0.4); c.bar.position.y = -0.4 - c.bar.scale.y / 2;
    const jaw = Math.max(0.12, D.clampX - grip.x + 0.06);
    c.hook.scale.x = jaw / 0.3; c.hook.position.set(-c.side * (jaw / 2 - 0.05), -reach + 0.05, 0);
  }
  for (const c of m.cables) c.mesh.position.z = D.slotZ + c.end * clampZ;
  m.payload = { kind, root, at: root ? root.position.clone() : new THREE.Vector3(), grip, clampZ, size, container: undefined, vehicle: undefined };
}

/** Let the payload go: moved under `into` (the ground's frame) where it hangs now, the clamps and cables free of it. */
export function releasePayload(m: DropshipModel, into: THREE.Object3D): void {
  const r = m.payload?.root;
  if (r && r.parent === m.mount) into.attach(r);
}
/** Take the payload back into the slot (a sheet's loop; a container picked up again). */
export function remountPayload(m: DropshipModel): void {
  const p = m.payload;
  if (!p?.root) return;
  m.mount.add(p.root);
  p.root.position.copy(p.at); p.root.rotation.set(0, 0, 0);
}

/** Carry the troop container (built if not given). */
export function mountContainer(m: DropshipModel, p: UnitPalette, c: TroopContainer = buildTroopContainer(p)): TroopContainer {
  const B = DROPSHIP.box;
  c.root.position.set(0, 0, 0);
  setPayload(m, 'troops', c.root, { y: B.height - 0.06, x: B.width / 2 + 0.08 }, DROPSHIP.clampZ, new THREE.Vector3(B.width, B.height, B.length));
  m.payload!.container = c;
  return c;
}

/**
 * Carry a built vehicle (`VehicleModel`, in the dropship's units): measured; null when it doesn't fit the slot. Its wheels
 * on the payload's floor, centred on the slot; the clamps take its hull's shoulders (the top of its body less a hand),
 * as wide as the body.
 */
export function mountVehicle(m: DropshipModel, v: VehicleModel): THREE.Vector3 | null {
  v.root.position.set(0, 0, 0);
  v.root.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(v.root.matrixWorld).invert();
  const all = new THREE.Box3(), body = new THREE.Box3(), tmp = new THREE.Box3();
  v.full.traverse(o => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.visible || o.userData.thin) return;
    tmp.setFromObject(mesh, true).applyMatrix4(inv);
    all.union(tmp);
    if (o.name === 'body') body.union(tmp);
  });
  const s = v.root.scale.x, size = all.getSize(new THREE.Vector3()).multiplyScalar(s);
  const S = DROPSHIP.slot;
  if (size.z > S.length || size.x > S.width || all.max.y * s > S.height) return null;
  const roof = (body.isEmpty() ? all.min.y + 0.65 * (all.max.y - all.min.y) : body.max.y) * s;
  const half = (body.isEmpty() ? (all.max.x - all.min.x) / 2 : (body.max.x - body.min.x) / 2) * s;
  v.root.position.set(0, -all.min.y * s, -((all.min.z + all.max.z) / 2) * s);
  setPayload(m, 'vehicle', v.root, { y: roof - 0.32, x: half + 0.03 }, Math.min(DROPSHIP.clampZ, size.z / 2 - 0.9), size);
  m.payload!.vehicle = v;
  return size;
}

/**
 * 69.3b's frame fit: carry a vehicle in its shipping frame (rendering/vehicleFrame.ts) — `framed` the frame and the vehicle
 * on its deck as one group (its origin the middle of the frame's floor, +z its front), `size` the frame's outside (FRAME:
 * along, across, up). The clamps take its top corner castings, as the container's top rails; null when it doesn't fit the slot.
 */
export function mountFramed(m: DropshipModel, framed: THREE.Object3D, size: { length: number; width: number; height: number }): THREE.Vector3 | null {
  const S = DROPSHIP.slot;
  if (size.length > S.length || size.width > S.width || size.height > S.height) return null;
  framed.position.set(0, 0, 0);
  const v = new THREE.Vector3(size.width, size.height, size.length);
  setPayload(m, 'vehicle', framed, { y: size.height - 0.06, x: size.width / 2 + 0.08 }, DROPSHIP.clampZ, v);
  return v;
}

/** Carry nothing. */
export function clearPayload(m: DropshipModel): void {
  setPayload(m, 'empty', null, { y: DROPSHIP.belly - 0.6, x: DROPSHIP.clampX - 0.2 }, DROPSHIP.clampZ, new THREE.Vector3());
}

// ---------------------------------------------------------------------------
// Build, pose, bake, cost
// ---------------------------------------------------------------------------

/** Build the dropship, `personHeight` world units per person, its payload slot empty. */
export function buildDropship(p: UnitPalette, personHeight: number): DropshipModel {
  const root = new THREE.Group(); root.scale.setScalar(personHeight / 1.8);
  const full = group(root), silhouette = group(root, 0, DROPSHIP.lift, 0), hull = group(full, 0, DROPSHIP.lift, 0);
  const m: DropshipModel = { root, full, silhouette, hull, tilters: [], fans: [], nacelles: [], glows: [], legs: [], slides: [], clamps: [], cables: [], mount: group(hull, 0, -DROPSHIP.lift, DROPSHIP.slotZ), pilots: [], payload: null, spin: 0 };
  airframe(m, p);
  liftJets(m, p); tailNozzles(m, p); nacelles(m, p);
  for (const side of [-1, 1]) for (const z of DROPSHIP.legZ) leg(m, p, side, z);
  for (const side of [-1, 1] as const) for (const end of [-1, 1] as const) clamp(m, p, side, end);
  // far tier: the body, cab, tail and sponsons as slabs, the nacelles as boxes
  slab(silhouette, BODY, 2 * DROPSHIP.half, p.body); slab(silhouette, CAB, 2.6, p.body); slab(silhouette, TAIL, 2.2, p.body);
  for (const sx of [-1, 1]) {
    slab(silhouette, SPONSON, 2 * DROPSHIP.sponsonHalf, p.body, sx * DROPSHIP.sponsonX);
    for (const z of DROPSHIP.nacelle.z) box(silhouette, 0.8, 0.95, 2.7, p.body, sx * DROPSHIP.nacelle.x, DROPSHIP.nacelle.y, z);
  }
  silhouette.visible = false;
  clearPayload(m);
  updateDropship(m, { tilt: 1, gear: 1, thrust: 0 }, 0);
  return m;
}

/**
 * 69.3b's follow-up (the owner: *"somehow tuning nacelle direction to animation, would be pretty cool with forward angle when
 * slowing down on entry, for exit not sure if you'd want to loop out or do a tight rotation"*): the nacelles' tilt from the
 * ship's motion along its heading (core/dropship.ts dropshipKinematics, people a second and a second²). `forward`: the most
 * past upright (a share of the quarter turn: 0.35 ≈ 32° ahead of down); `brake` / `boost`: the deceleration and acceleration
 * at which the tip is whole (the approach brakes at ~5.5, the pivot's way out speeds up at ~5.5, the loop's at ~13); `noise`:
 * what is ignored (cruise wanders ±0.8 from the poses' differencing); `tau`: the easing's time constant (seconds).
 */
export const NACELLE_TILT = { forward: 1.35, brake: 5, boost: 5, noise: 1.5, tau: 0.5 };
/**
 * The nacelles' tilt for the ship's speed and acceleration along its heading (`cruise`: its cruise speed): upright at a
 * standstill and aft at cruise (by the speed, as the host had it); tipped forward toward NACELLE_TILT.forward while it brakes
 * (thrust ahead, the approach's slowing into the landing); turned aft ahead of its speed while it speeds up (the way out,
 * by either exit). The host eases toward it (`easeTilt`).
 */
export function nacelleTiltFor(speed: number, accel: number, cruise: number): number {
  const N = NACELLE_TILT, k = (x: number) => Math.min(1, Math.max(0, x));
  const base = k(1 - Math.abs(speed) / cruise);
  const brake = speed > 0.5 ? k((-accel - N.noise) / (N.brake - N.noise)) : 0;
  const boost = k((accel - N.noise) / (N.boost - N.noise));
  return brake > 0 ? base + (N.forward - base) * brake : base * (1 - 0.5 * boost);
}
/** Ease a drawn tilt toward its target over `dt` seconds (an exponential approach: no snaps however the target jumps). */
export function easeTilt(current: number, target: number, dt: number): number {
  return current + (target - current) * (1 - Math.exp(-Math.max(0, dt) / NACELLE_TILT.tau));
}

/** Set the moving parts to `s`; `dt` turns the fans. */
export function updateDropship(m: DropshipModel, s: DropshipMotion, dt: number): void {
  const k = (v: number | undefined, d: number) => Math.min(1, Math.max(0, v ?? d));
  const thrust = k(s.thrust, 0.6), gear = k(s.gear, 1), clamps = k(s.clamps, 0), ramp = k(s.ramp, 0);
  const ease = (x: number) => x * x * (3 - 2 * x);
  // the tilt: eased from aft (0) to upright (1); past upright it runs on straight (the nacelles and tail nozzles tipped ahead
  // of down, the louvres vectoring forward) to NACELLE_TILT.forward
  const tiltAll = Math.min(NACELLE_TILT.forward, Math.max(0, s.tilt ?? 1)), tilt = Math.min(1, tiltAll);
  const turned = tiltAll <= 1 ? ease(tiltAll) : tiltAll;
  for (const t of m.tilters) t.group.rotation.x = t.hover * turned + (t.cruise ?? 0) * (1 - turned);
  m.spin += dt * (6 + 40 * thrust);
  for (const f of m.fans) f.rotation.z = m.spin;
  for (const g of m.glows) {
    const share = g.phase === 'both' ? thrust : g.phase === 'lift' ? thrust * tilt : thrust * (1 - tilt);
    g.mesh.visible = share > 0.03;
    g.mesh.scale.setScalar(0.5 + 0.6 * share);
    (g.mesh.material as THREE.MeshBasicMaterial).opacity = 0.2 + 0.7 * share;
  }
  for (const l of m.legs) l.rotation.x = -(Math.PI / 2) * (1 - ease(gear));
  for (const sl of m.slides) sl.group.position.y = sl.retract * (1 - ease(gear));
  for (const c of m.clamps) c.group.rotation.z = c.side * 0.9 * ease(clamps);
  m.hull.rotation.z = s.bank ?? 0;
  m.hull.rotation.x = -(s.pitch ?? 0);
  const lower = Math.max(0, s.lower ?? 0);
  m.mount.position.y = -DROPSHIP.lift - lower;
  const grip = m.payload?.grip.y ?? DROPSHIP.belly;
  const top = DROPSHIP.belly - 0.1, bottom = grip - lower + 0.05;
  for (const c of m.cables) {
    const len = Math.max(0.01, top - bottom);
    c.mesh.visible = !!s.cables && !!m.payload && m.payload.kind !== 'empty' && len > 0.15;
    c.mesh.scale.y = len;
    c.mesh.position.y = top - len / 2 - DROPSHIP.lift;   // (in the hull's frame)
  }
  const cont = m.payload?.container;
  if (cont) cont.ramp.rotation.x = rampDown(cont.ramp.userData.len as number) * ease(ramp);
}

/**
 * 63.16 (the owner: *"you can walk through the door of the dropship dropbox even when it's closed, and it never opens except
 * for the dismount animation, should be left open for mount and while waiting for takeoff"*): a drawn ramp moved toward its
 * target (0 shut … 1 down) at the hydraulics' pace — DROPSHIP_ANCHORS.seconds' rampOpen to let it down, rampShut to close it —
 * so the host draws it as the sim's container stands (open while a ship stands at it, shut in flight) without a snap.
 */
export function easeRamp(current: number, target: number, dt: number): number {
  const S = DROPSHIP_ANCHORS.seconds, d = Math.max(0, dt);
  return target > current ? Math.min(target, current + d / S.rampOpen) : Math.max(target, current - d / S.rampShut);
}

/** Set a free container's ramp (one left on the ground). */
export function setContainerRamp(c: TroopContainer, share: number): void {
  const x = Math.min(1, Math.max(0, share));
  c.ramp.rotation.x = rampDown(c.ramp.userData.len as number) * x * x * (3 - 2 * x);
}

/** The groups that move (each bakes on its own); `clampsFixed`, the clamps left out (they never open). */
function movingGroups(m: DropshipModel, clampsFixed = false): THREE.Object3D[] {
  return [m.hull, ...m.tilters.map(t => t.group), ...m.fans, ...m.legs, ...m.slides.map(s => s.group), ...(clampsFixed ? [] : m.clamps.map(c => c.group))];
}
/**
 * Fold the static parts into one mesh per material in each moving group, as `bakeVehicleModel` does (the payload is baked by its
 * owner). `clampsFixed` (69.98, DECISIONS 319: a gunship carries nothing, its clamps never open nor reach for a load): the clamps
 * fold into the hull's meshes with the rest — about 12 draw calls a gunship.
 */
export function bakeDropship(m: DropshipModel, clampsFixed = false): number {
  const moving = movingGroups(m, clampsFixed);
  const under = (o: THREE.Object3D, r: THREE.Object3D) => { for (let q = o.parent; q; q = q.parent) if (q === r) return true; return false; };
  const keep = [m.mount, ...(clampsFixed ? [] : m.clamps.flatMap(c => [c.bar, c.hook]))];
  let n = 0;
  for (const g of moving) n += bakeStaticParts(g, { exclude: [...moving.filter(o => o !== g && under(o, g)), ...keep] }).partsBaked;
  m.silhouette.visible = true; n += bakeStaticParts(m.silhouette).partsBaked; m.silhouette.visible = false;
  return n;
}
/** Bake a container's static parts (its shell; the ramp on its own). */
export function bakeContainer(c: TroopContainer): number {
  return bakeStaticParts(c.root, { exclude: [c.ramp] }).partsBaked + bakeStaticParts(c.ramp).partsBaked;
}

/** Triangles and draws at the full tier under `root` (`skip` left out: a payload); a glow or cable counts while shown. */
export function dropshipCost(root: THREE.Object3D, skip?: THREE.Object3D): { triangles: number; draws: number } {
  let triangles = 0, draws = 0;
  root.traverse(o => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    for (let q: THREE.Object3D | null = o; q && q !== root; q = q.parent) if (q === skip || !q.visible) return;
    draws++;
    triangles += mesh.geometry.index ? mesh.geometry.index.count / 3 : mesh.geometry.attributes.position.count / 3;
  });
  return { triangles, draws };
}
